import * as React from "react";
import {cx} from "emotion";

import "./display-name.scss";

import {bindInput}           from "@Common/react/bind-input";
import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {resolveErrorMessage} from "@Common/react/cs-form-2/resolve-error-message";
import {maxLength}           from "@Common/react/cs-form-2/validators/max-length";
import {notBlank}            from "@Common/react/cs-form-2/validators/not-blank";
import {fragments}           from "@Common/react/fragments";
import {Invoke}              from "@Common/react/invoke";
import {keyed}               from "@Common/react/keyed";
import {scopedState}         from "@Common/react/scoped-state";
import {scnv}                from "@Common/react/state-change-nested-value";
import {Static2}             from "@Common/react/static-2";
import {Trimmer}             from "@Common/react/trimmer";
import {UseState}            from "@Common/react/use-state";
import {cnv}                 from "@Common/utils/arr-path";
import {chain}               from "@Common/utils/fs";

import {plural}            from "../../../../../../language/plural";
import {ErrorHighlighter}  from "../../../../../../light/error-highlighter";
import {scrollIntoView}    from "../../../../../../light/scroll-into-view";
import {setCurrentToSaved} from "../../../set-current-to-saved";

export const DisplayName = ({agentId, initData, next}) => cs(
    consumeContext("apis"),
    consumeContext("session"),
    ["name", (_, next) => UseState({
        getInitValue: () => chain(
            initData?.displayName,
            (_) => ({
                saved: _,
                current: _,
            }),
        ),
        next,
    })],
    ({name, session, apis}, next) => fragments(
        next(),
        session?.data?.agent && cs(
            keyed(agentId ?? session.data.agent.idAgent),
            () => Invoke({
                action: async () => {
                    if (name.value.current) {
                        return;
                    }

                    if (agentId != null && agentId !== session.data.agent.idAgent) {
                        const agent = await apis.next.session.getAgent({agentId});
                        scnv(name, ["current"], () => getAgentDisplayName(agent));
                    } else {
                        scnv(name, ["current"], () => getAgentDisplayName(session.data.agent));
                    }
                },
            }),
        ),
    ),

    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],

    ({name, eh, domRef}) => {

        const error = resolveErrorMessage(name.value.current, [notBlank, maxLength(40)]);

        return next({
            editor: {
                valid: !error,

                $name: "Informed Delivery > EmailProof > Display Name",
                changed: name.value?.saved !== name.value?.current,
                prepareSave: (data) => ({
                    newValue: chain(
                        data,
                        (_) => cnv(_, ["displayName"], () => name.value?.current),
                    ),
                    onSaved: () => setCurrentToSaved(name),
                }),

                errorsHandling: {
                    highlightErrors: () => {
                        scrollIntoView(domRef.get());
                        eh.start();
                    },
                },
            },
            renderPreview: ({className}) => (
                <div className={cx("display-name-343 preview", className)}>
                    <div className="label">
                        FROM:
                    </div>
                    <div {...{
                        className: "value",

                        "xd-qa-type": "text",
                        "xd-qa-name": "Display Name Preview",
                    }}>
                        {name.value.current}
                    </div>
                </div>
            ),
            renderEdit: ({showErrors}) => (
                <div className="display-name-343 editing">
                    <div {...{
                        className: "control-label",

                        "xd-qa-type": "label",
                        "xd-qa-name": "Display Name",
                    }}>
                        Display Name<span className="asterisk">*</span>
                    </div>

                    {cs(
                        ["buffer", (_, next) => Trimmer({
                            state: scopedState(name, ["current"]),
                            next,
                        })],
                        ({buffer}) => (
                            <input {...{
                                ...bindInput(buffer.state),
                                onBlur: buffer.flush,
                                ref: domRef.set,
                                className: cx(eh.css, {hasError: showErrors && error}),

                                "xd-qa-type": "text_field",
                                "xd-qa-name": "Display Name",
                            }}/>
                        )
                    )}
                    {showErrors && error ? (
                        <div {...{
                            className: "error",
                            "xd-qa-type": "text",
                            "xd-qa-name": "Display Name Length Error",
                        }}>
                            {error}
                        </div>
                    ) : (
                        <div {...{
                            className: "length-hint",

                            "xd-qa-type": "text",
                            "xd-qa-name": "Display Name Length Hint",
                        }}>
                            {chain(
                                name.value.current?.length ?? 0,
                                (_) => _ <= 40 ? (
                                    `${40 - _} ${plural("Character", 40 - _)} Remaining`
                                ) : (
                                    `Exceeded ${_ - 40} ${plural("character", _ - 40)}`
                                ),
                            )}

                        </div>
                    )}
                </div>
            ),
        });
    },
);

const getAgentDisplayName = (agent) => {
    if (agent.agentDisplay1?.trim()) {
        return agent.agentDisplay1.trim();
    }
    return [
        agent.agentNameFirst,
        agent.agentNameMiddle,
        agent.agentNameLast,
    ].filter(v => v?.trim()).map(v => v.trim()).join(" ");
};


